import { styled } from "@pigment-css/react";
import { CrossLarge } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { TopNav } from "@acto/taskpane/field-sales-2/components/patterns/TopNav";
import { HeaderIconButton } from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";
import { CyclingLoaderPill } from "@acto/taskpane/field-sales-2/components/patterns/CyclingLoaderPill";

interface RecapAnimationScreenProps {
  /** Customer name — the pill's opening text, before it cycles the generation steps. */
  customerName: string;
  isSummarizing: boolean;
  /** Back/close affordance so a slow summarize never traps the user here. */
  onClose: () => void;
}

// Generation-step keys under `fieldSalesTwo.recaps.animation.steps`, cycled after the name.
const STEP_KEYS = ["analyzing", "summarizing", "tasks", "finishing"] as const;

const Page = styled("main")(({ theme }) => ({
  flex: 1,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  background: theme.fieldSalesTwo.colors.background,
}));

const Body = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[24],
  padding: `0 ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[24]}`,
}));

/**
 * Presentational "Animation" screen. The title pill opens with the customer name, then crossfades
 * through the generation steps on a loop while `isSummarizing`. Both leaving on success and
 * leaving on failure are owned by the parent, so this screen renders no outcome of its own.
 */
export function RecapAnimationScreen({ customerName, isSummarizing, onClose }: RecapAnimationScreenProps) {
  const t = useTranslations("fieldSalesTwo.recaps");

  const steps = STEP_KEYS.map((key) => t(`animation.steps.${key}`));

  return (
    <Page>
      <TopNav
        leading={
          <HeaderIconButton type="button" aria-label={t("a11y.close")} onClick={onClose}>
            <CrossLarge width={18} height={18} />
          </HeaderIconButton>
        }
      />

      <Body>
        <CyclingLoaderPill leadText={customerName} stages={steps} active={isSummarizing} />
      </Body>
    </Page>
  );
}
